// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import {Component, OnInit, QueryList, ViewChildren} from '@angular/core';
import {UserService} from '../../../services/user-service';
import {
  DeactivateUsersResponse,
  DeactivateUsersStatus,
  ICreateUser,
  IManageUser,
  IUser,
  SearchUsersResponse,
  UserRole,
  UserSource
} from '../../../types/user';
import {HeaderService} from '../../header.service';
import {SelectionModel} from '@angular/cdk/collections';
import {MatDialog} from '@angular/material/dialog';
import {MatSort} from '@angular/material/sort';
import { BehaviorSubject, combineLatest, EMPTY, forkJoin, Observable, of, throwError } from 'rxjs';
import {AlertDialogComponent} from '../../dialogs/alert/alert.component';
import {AddPeerUserDialogComponent} from './add-peer-user-dialog/add-peer-user-dialog.component';
import {EditPeerUserDialogComponent} from './edit-peer-user-dialog/edit-peer-user-dialog.component';
import {NotificationService} from '../../../services/notification-service';
import {SelectedOrgMethod} from '../../../shared/selectedOrg';
import {D3eService} from 'src/services/d3e-service';
import {IColumnChooserElement} from '../../shared/components/column-chooser/column-chooser.component';
import {TranslateService} from '@ngx-translate/core';
import { catchError, filter, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import {SecurityRequestService} from '../../../services/security-request.service';
import {ISecurityRequest, SecurityApproverState, SecurityRequestType} from '../../../types/security-requests';
import {RestConfigService} from '../../../services/config-service';
import {AdminSettingsService} from '../../../services/admin-settings.service';
import {IGroup, OrgType} from '../../../types/group';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import authHelper from '../../auth/authHelper';
import {Store} from '@ngrx/store';
import {AppState} from '../../store/app.state';
import {selectUser} from '../../store/user/user.selectors';
import {ActivatedRoute} from '@angular/router';
import multiDownload from 'multi-download';
import {getActiveStatusTranslationMap, getApproverStatusTranslationMap, getYesNoTranslationMap} from '../../../shared/translation_maps';
import {SecurityRequestStatusDialogComponent} from '../encryption/security-request-status-dialog/security-request-status-dialog.component';

const getIdentitySourceTranslationMap = () => {
  return Object.keys(UserSource).reduce((acc, val) => {
    acc[val] = UserSource[val];
    return acc;
  }, {});
};

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-manage-users',
    templateUrl: './manage-users.component.html',
    styleUrls: ['./manage-users.component.scss'],
    standalone: false
})
export class ManageUsersComponent implements OnInit {
  users: {checked: boolean; userData: IManageUser}[] = [];
  displayedColumns = [];
  loadingUsers$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  selection = new SelectionModel<IUser>(true, []);
  selectedGroup: IGroup;
  isEffectiveUserAdmin: boolean;
  isRealUserAdmin: boolean;
  isHelpDesk: boolean;
  searchTerm = '';
  selectedOrgId: string;
  hasUserBeenSet = false;
  @ViewChildren(MatSort) sort = new QueryList<MatSort>();
  columns: IColumnChooserElement[] = [];
  expirable = false;
  queryPageStart = 0;
  queryPageSize = 50;
  totalResults = 0;
  sortAttrs = [{'sortAttr': 'username', 'sortOrder': 'asc'}];

  valueStyle = {
    display: 'flex',
    height: '20px',
    width: '50px',
    margin: '0',
    padding: '0'
  };

  user$: Observable<IManageUser> = this.store.select(selectUser());

  fSearch: string;
  fAdd: string;
  fReactivate: string;
  fDeactivate: string;
  fUsername: string;
  fFullName: string;
  fUsers: string;
  fLocation: string;
  fJob: string;
  fDepartment: string;
  fKey: string;
  fSource: string;
  fLogin: string;
  fRole: string;
  fStatus: string;
  fNoPermission: string;
  fYes: string;
  fNo: string;
  fDeactivateTitle: string;
  fDeactivateContent: string;
  fReactivateTitle: string;
  fReactivateContent: string;
  fDeactivateOwnError: string;
  fDeactivateUnknownError: string;
  fUserUpdated: string;
  fUnknownError: string;
  fActive: string;
  fInactive: string;
  fAdmin: string;
  fUser: string;
  fNever: string;
  fN_A: string;
  fNone: string;
  fPublicKey: string;
  fPublicPrivateKey: string;
  fPublicProtectedPrivateKey: string;
  fNonCigentUser: string;
  fIdentitySource: string;

  title: string;
  constructor(
      private userService: UserService,
      private headerService: HeaderService,
      public dialog: MatDialog,
      private notification: NotificationService,
      public d3eService: D3eService,
      private translate: TranslateService,
      private adminSettingsService: AdminSettingsService,
      private securityRequestService: SecurityRequestService,
      private restConfigService: RestConfigService,
      private readonly route: ActivatedRoute,
      private readonly store: Store<AppState>,
  ) {
    headerService.setContextHelp(['help.users']);
    this.translate.get(['U_SEARCH_PHOLDER', 'U_ADD_TIP', 'U_REACTIVATE', 'U_DEACTIVATE', 'U_USERS',
      'U_FNAME', 'U_USER', 'U_LOCATION', 'U_JOB', 'U_DEPARTMENT', 'U_KEY', 'U_SOURCE', 'U_LAST_LOGIN',
      'U_ROLE', 'U_STATUS', 'TITLE_USERS', 'U_NO_PERMISSION', 'YES', 'NO', 'U_DEACTIVATE_TITLE', 'U_DEACTIVATE_CONTENT',
      'U_REACTIVATE_TITLE', 'U_REACTIVATE_CONTENT', 'U_DEACTIVATE_OWN_ERROR',
      'U_DEACTIVATE_UNKNOWN_ERROR', 'U_USER_UPDATED', 'ERROR_OCCURRED_UNKNOWN', 'ACTIVE', 'INACTIVE', 'ADM_ADMIN', 'USER',
      'NEVER', 'N_A', 'NONE', 'PUBLIC_KEY', 'PUBLIC_PRIVATE_KEY', 'PUBLIC_PROTECTED_PRIVATE_KEY',
      'TOOLTIP_NON_CIGENT_USER', 'IDENTITY_SOURCE', 'SELECTOR_YOUR_USERS']).subscribe(vals => {
        this.fSearch = vals['U_SEARCH_PHOLDER'];
        this.fAdd = vals['U_ADD_TIP'];
        this.fReactivate = vals['U_REACTIVATE'];
        this.fDeactivate = vals['U_DEACTIVATE'];
        this.fUsers = vals['U_USERS'];
        this.fFullName = vals['U_FNAME'];
        this.fUsername = vals['U_USER'];
        this.fLocation = vals['U_LOCATION'];
        this.fJob = vals['U_JOB'];
        this.fDepartment = vals['U_DEPARTMENT'];
        this.fKey = vals['U_KEY'];
        this.fSource = vals['U_SOURCE'];
        this.fLogin = vals['U_LAST_LOGIN'];
        this.fRole = vals['U_ROLE'];
        this.fStatus = vals['U_STATUS'];
        this.fNoPermission = vals['U_NO_PERMISSION'];
        this.fYes = vals['YES'];
        this.fNo = vals['NO'];
        this.fDeactivateTitle = vals['U_DEACTIVATE_TITLE'];
        this.fDeactivateContent = vals['U_DEACTIVATE_CONTENT'];
        this.fReactivateTitle = vals['U_REACTIVATE_TITLE'];
        this.fReactivateContent = vals['U_REACTIVATE_CONTENT'];
        this.fDeactivateOwnError = vals['U_DEACTIVATE_OWN_ERROR'];
        this.fDeactivateUnknownError = vals['U_DEACTIVATE_UNKNOWN_ERROR'];
        this.fUserUpdated = vals['U_USER_UPDATED'];
        this.fUnknownError = vals['ERROR_OCCURRED_UNKNOWN'];
        this.fActive = vals['ACTIVE'];
        this.fInactive = vals['INACTIVE'];
        this.fAdmin = vals['ADM_ADMIN'];
        this.fUser = vals['USER'];
        this.fNever = vals['NEVER'];
        this.fN_A = vals ['N_A'];
        this.fNone = vals['NONE'];
        this.fPublicKey = vals['PUBLIC_KEY'];
        this.fPublicPrivateKey = vals['PUBLIC_PRIVATE_KEY'];
        this.fPublicProtectedPrivateKey = vals['PUBLIC_PROTECTED_PRIVATE_KEY'];
        this.fNonCigentUser = vals['TOOLTIP_NON_CIGENT_USER'];
        this.fIdentitySource = vals['IDENTITY_SOURCE'];

        this.title = vals['TITLE_USERS'];

        this.columns = [
          { title: 'select', value: 'select', selected: true, hidden: true, apiName: null, },
          { title: this.fFullName, value: 'fullname', selected: true, hidden: false, apiName: 'fullname', },
          { title: this.fUsername, value: 'username', selected: true, hidden: false, apiName: 'username', },
          { title: this.fLocation, value: 'location', selected: false, hidden: false, apiName: 'location', },
          { title: this.fJob, value: 'jobTitle', selected: false, hidden: false, apiName: 'title', },
          { title: this.fDepartment, value: 'department', selected: false, hidden: false, apiName: 'department', },
          { title: this.fKey, value: 'keyStatus', selected: true, hidden: false, },
          { title: this.fSource, value: 'keySource', selected: true, hidden: false, },
          { title: this.fLogin, value: 'lastLogin', selected: true, hidden: false, apiName: 'last_login', },
          { title: this.fIdentitySource, value: 'identitySource', selected: true, hidden: false, apiName: 'source', txMap: getIdentitySourceTranslationMap(), },
          { title: this.translate.instant('EXPIRED'), value: 'passwordExp', selected: true, hidden: false,  apiName: 'password_exp', },
          { title: this.fRole, value: 'role', selected: true, hidden: false, apiName: 'role', },
          { title: this.fStatus, value: 'active', selected: true, hidden: false, apiName: 'active', txMap: getActiveStatusTranslationMap(this.translate), },
          { title: this.translate.instant('security-approval'), value: 'securityApprover', selected: false, hidden: false, apiName: 'security_approver', txMap: getApproverStatusTranslationMap(this.translate), },
          { title: this.translate.instant('2FA'), value: 'twoFactor', selected: false, hidden: false, apiName: null, txMap: getYesNoTranslationMap(this.translate), },
        ];

        this.headerService.setOrgSelectorDefaultOption(vals['SELECTOR_YOUR_USERS']);

    });
    this.headerService.setTitle(this.title);
    this.headerService.setSubtitle('');
    this.headerService.setIcon('mdi-account-multiple');
    this.headerService.showUserFilter(true);
    this.loadingUsers$.next(true);

    this.isEffectiveUserAdmin = false;
    this.isRealUserAdmin = false;

    route.queryParamMap.subscribe(queryParams => {
      const searchParam = queryParams.get('search');
      if (!!searchParam) {
        this.searchTerm = searchParam;
        this.getUsers(true);
      }
    });
  }

  getSelectedOrg() {
    let getOrg: Observable<IGroup>;
    if (this.selectedOrgId === 'All') {
      getOrg = this.userService.get_organization(authHelper.getGroupFromToken());
    } else {
      getOrg = this.userService.get_organization(+this.selectedOrgId);
    }
    forkJoin({
      user: this.user$.pipe(take(1)),
      selectedGroupResp: getOrg,
    })
      .pipe(untilDestroyed(this))
      .subscribe(({user, selectedGroupResp}) => {
        this.selectedGroup = selectedGroupResp;
        const mspUserId: number = user.id;
        const selectedGroupId: number = selectedGroupResp.id;
        const isMSP: boolean = user.org_type && user.org_type.value === OrgType.MSP;
        const isReseller: boolean = user.org_type && user.org_type.value === OrgType.RESELLER;
        const isMSPManagingOrg = isMSP && mspUserId !== selectedGroupId;
        const isResellerManagingMSP = isReseller && mspUserId != selectedGroupId;
        // assume all selected orgs are effective user admins
        this.isEffectiveUserAdmin = selectedGroupId === user.group_id || isMSPManagingOrg || isResellerManagingMSP;
        this.isRealUserAdmin = user.role == UserRole.ADMIN;
        this.isHelpDesk = user.role == UserRole.HELP_DESK;
        this.getUsers(true);
      });
  }

  getDisplayColumns(): string[] {
    return this.displayedColumns.filter(c => this.isEffectiveUserAdmin || !this.isEffectiveUserAdmin && c !== 'select');
  }

  ngOnInit() {
    this.headerService.orgSelectedChanged
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        this.selectedOrgId = value;
        this.selection.clear();
        if ( this.hasUserBeenSet ) {
          this.getSelectedOrg();
        }
        this.loadingUsers$.next(true);
        this.hasUserBeenSet = true;
        this.queryPageStart = 0;
        this.totalResults = 0;
    });
    this.getSelectedOrg();

    this.securityRequestService.updated$.pipe(
      untilDestroyed(this),
    ).subscribe(() => {
        this.getUsers(true);
    });
  }

  getKeyStatusText(user) {
    // Note: These user-friendly strings exist in the REST service for searching (all lower case).
    //       If you change strings here, also change them in the REST api.
    if (user.protected_key) {
      return this.fPublicProtectedPrivateKey;
    }
    if (user.private_key) {
      return this.fPublicPrivateKey;
    }
    if (user.public_key) {
      return this.fPublicKey;
    }
    return this.fNone;
  }

    onScrollHandler() {
        this.queryPageStart += this.queryPageSize;
        this.getUsers();
    }

    sortColumn(event) {
        const sort = {
            'sortAttr': event.active,
            'sortOrder': event.direction
        };
        this.sortAttrs = [sort];
        this.queryPageStart = 0;
        this.getUsers(true);
    }

  getUsers(clearCache = false) {
    this.loadingUsers$.next(true);
    if (clearCache) {
        this.users = [];
        this.queryPageStart = 0;
        this.totalResults = 0;
    }
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
    const query = this.getQuery()
    if (this.shouldShowUsers()) {
        this.userService.api_search_users_infinite_scroll(query, selectedOrg)
        .pipe(
            tap((response: SearchUsersResponse) => {
                const usersRowList = [] as {checked: boolean; userData: IManageUser}[];
                response.users.forEach(u => usersRowList.push({checked: this.isAllSelected(), userData: u}))
                this.totalResults = response.total;
                if (!clearCache) {
                    this.users = [...this.users, ...usersRowList];
                } else {
                    this.users = usersRowList;
                }
            }),
            catchError(() => {
                this.users = [];
                this.loadingUsers$.next(false);
                this.notification.showError(this.fUnknownError);
                return EMPTY;
            }),
            finalize(() => {
                this.setExpirable();
                this.selection.clear();
                this.loadingUsers$.next(false);
            }),
            untilDestroyed(this)
        ).subscribe();
    } else {
      this.loadingUsers$.next(false);
    }
  }

    getQuery() {
        const query = {
            'searchTerm': this.searchTerm,
            'pageStart': this.queryPageStart,
            'pageSize': this.queryPageSize
        };

        if (this.sortAttrs.length) {
            const sorts = [];
            for (const item of this.sortAttrs) {
                const sort = {
                    'attr': item['sortAttr'],
                    'desc': (item['sortOrder'] === 'desc')
                };
                switch (sort['attr']) {
                    case 'fullname':
                        sort['attr'] = 'fname';
                        break;
                    case 'username':
                        sort['attr'] = 'username';
                        break;
                    case 'location':
                        sort['attr'] = 'location';
                        break;
                    case 'jobTitle':
                        sort['attr'] = 'title';
                        break;
                    case 'department':
                        sort['attr'] = 'department';
                        break;
                    case 'keyStatus':
                        sort['attr'] = 'key_status';
                        break;
                    case 'keySource':
                        sort['attr'] = 'key_source';
                        break;
                    case 'lastLogin':
                        sort['attr'] = 'last_login';
                        break;
                    case 'identitySource':
                        sort['attr'] = 'source';
                        break;
                    case 'role':
                        sort['attr'] = 'role';
                        break;
                    case 'passwordExp':
                        sort['attr'] = 'password_exp';
                        break;
                    case 'active':
                        sort['attr'] = 'active';
                        break;
                    case 'securityApprover':
                        sort['attr'] = 'security_approver';
                        break;
                    case 'twoFactor':
                        sort['attr'] = 'second_factor_enabled';
                        break;
                    default:
                        continue;
                }
                sorts.push(sort);
            }
            if (sorts.length) {
                query['sorts'] = sorts;
            }
        }

        return query;
    }

  shouldShowUsers() {
    return this.loadingUsers$.value || this.isRealUserAdmin || this.isHelpDesk || this.selectedOrgId !== 'All';
  }

  canActivate(): boolean {
    if (!this.isEffectiveUserAdmin) {
      return false;
    }
    return this.selection.selected.filter(e => !e.active).length > 0;
  }

  canDeactivate(): boolean {
    if (!this.isEffectiveUserAdmin) {
      return false;
    }
    if (this.selection.selected.filter(e => e.security_approver).length) {
      return false;
    }
    return this.selection.selected.filter(e => e.active).length > 0;
  }

  canDelete(): Observable<boolean> {
    return this.user$.pipe(
      map(user => {
        return this.selection.selected.length > 0 && this.selection.selected.filter(e => e.id === user.id).length == 0;
      })
    );
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.users.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
        this.selection.clear() :
        this.users.forEach(user => {
          user.checked = true;
          this.selection.select(user.userData);
        });
    this.setExpirable();
  }

  checkboxToggle($event, row) {
    this.selection.toggle(row);
    this.setExpirable();
  }

  setExpirable() {
    const length = this.selection.selected.length;
    const now = new Date(Date.now());
    const expirable = this.selection.selected.find(user => new Date(user.password_exp) > now);
    this.expirable = length > 0 && expirable !== undefined;
  }

  checkboxLabel(row: IManageUser) {
      return `select ${row.fname} ${row.lname}`;
  }

  addUser() {
    forkJoin({
      restConfig: this.restConfigService.getRestConfig(),
      adminSettings: this.adminSettingsService.getAdminSettings(),
      user: this.user$.pipe(take(1)),
    }).pipe(
        switchMap(({restConfig, adminSettings, user}) => {
          const orgType = user.org_type.value;
          const isMspOrReseller = orgType === OrgType.RESELLER || orgType === OrgType.MSP;
          return this.dialog.open(AddPeerUserDialogComponent, {
            height: 'auto',
            disableClose: true,
            data: {
              showSecurityApproverCheckbox: restConfig.security_requests_enabled && adminSettings.enable_recovery_key,
              disableApproverCheckbox: isMspOrReseller,
            }
          }).afterClosed();
        }),

        switchMap(newUserPostData => {
          if (newUserPostData) {
            const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
            const vals = newUserPostData.values;
            const notifyApprovers = vals.notifyApprovers;
            const newUser: ICreateUser = {
              firstName: vals.fname,
              lastName: vals.lname,
              department: vals.department,
              location: vals.location,
              email: vals.email,
              email_notify_language: vals.email_notify_language,
              username: vals.email,
              title: vals.title,
              group_id: this.selectedGroup.id,
              role: vals.role,
              invite: vals.sendEmail,
              generateKeys: vals.generateKeys,
              keyPassword: vals.keyPassword,
              security_approver: vals.security_approver,
              org_type: null,
            };
            const userAdd$ = this.userService.api_post_user(newUser, selectedOrg).pipe(
                switchMap(postedUser => {
                  if (newUser.security_approver) {
                    return this.securityRequestService.postSecurityRequest(
                        postedUser.id, SecurityRequestType.ADD_APPROVER, null, null, notifyApprovers);
                  }
                  return of(null);
                }),
                catchError(() => {
                  this.notification.showError(this.fUnknownError);
                  return EMPTY;
                }),
                finalize(() => {
                  this.getUsers(true);
                })
            );
            if (newUserPostData.addMore) {
              this.addUser();
            }
            return userAdd$;
          } else {
            return EMPTY;
          }
        }))
      .subscribe(() => {
        this.securityRequestService.refresh$.next(null);
        this.notification.showSuccess(this.translate.instant('CREATED_USER'));
    });
  }

    editUser(userId: number) {
        forkJoin({
            restConfig: this.restConfigService.getRestConfig(),
            adminSettings: this.adminSettingsService.getAdminSettings(),
            approversResult: this.securityRequestService.getApproversByUser(userId),
            user: this.userService.api_get_user_by_id(userId),
            loggedInUser: this.user$.pipe(take(1)),
        }).pipe(
            switchMap(({restConfig, adminSettings, approversResult, user, loggedInUser}) => {
                if (!approversResult.success) {
                    return throwError(() => new Error('Unable to fetch approvers.'));
                }

                const orgType = loggedInUser.org_type.value;
                const isMspOrReseller = orgType === OrgType.RESELLER || orgType === OrgType.MSP;
                const disableApproverCheckbox =
                    (approversResult.results.length === 1 && approversResult.results[0].userId === user.id) ||
                    loggedInUser.group_id !== user?.group_id || isMspOrReseller;

                const dialogRef = this.dialog.open(EditPeerUserDialogComponent, {
                    maxHeight: '80vh',
                    data: {
                        user,
                        isSelf: user.id === loggedInUser.id,
                        isLastAdmin: user.role === UserRole.ADMIN &&
                            this.users.map(u => u.userData).reduce((acc: number, u: IManageUser) =>
                                u.role === UserRole.ADMIN ? acc + 1 : acc, 0) < 2,
                        showSecurityApproverCheckbox: restConfig.security_requests_enabled && adminSettings.enable_recovery_key,
                        disableApproverCheckbox,
                        sameOrg: user.group_id === loggedInUser.group_id,
                    },
                });

                return dialogRef.afterClosed().pipe(map(newUser => ({user, newUser})));
            }),
            switchMap(({user, newUser}) => {
                if (!newUser) return EMPTY;

                let result: Observable<void> = of(undefined);

                if (newUser.security_approver !== undefined &&
                    Boolean(user.security_approver) !== Boolean(newUser.security_approver) &&
                    user.security_approver !== SecurityApproverState.APPROVER_PENDING &&
                    user.security_approver !== SecurityApproverState.NOT_APPROVER_PENDING
                ) {
                    const srType = newUser.security_approver
                        ? SecurityRequestType.ADD_APPROVER
                        : SecurityRequestType.REMOVE_APPROVER;
                    result = this.securityRequestService.postSecurityRequest(user.id, srType, null, null, newUser.notifyApprovers);
                }

                return combineLatest([of(newUser), result]);
            }),
            switchMap(([newUser]) => {
                if (!newUser) return EMPTY;
                const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
                return this.userService.api_put_user(newUser, selectedOrg);
            }),
            catchError(() => {
                this.notification.showError(this.fUnknownError);
                return EMPTY;
            })
        ).subscribe({
            next: () => {
                this.notification.showSuccess(this.fUserUpdated);
                this.securityRequestService.refresh$.next(null);
                this.getUsers(true);
            },
            error: () => this.notification.showError(this.fUnknownError),
        });
    }

  deactivateSelectedUsers() {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      data: {
        title: this.fDeactivateTitle,
        content: this.fDeactivateContent,
        rejectBtnName: this.fNo,
        acceptBtnName: this.fYes,
        showRejectBtn: true,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
        const user_ids = this.selection.selected.map(user => user.id);

        this.userService.api_users_deactivate(user_ids, selectedOrg)
        .pipe(
          tap((response: DeactivateUsersResponse) => {
              switch (response.status) {
                  case DeactivateUsersStatus.SUCCESS:
                      this.getUsers(true);
                      this.selection.clear();
                      break;
                  case DeactivateUsersStatus.NOT_PERMITTED:
                      this.notification.showError(this.translate.instant('U_DEACTIVATE_PERMISSION_ERROR'));
                      break;
                  case DeactivateUsersStatus.SELF_BLOCKED:
                      this.notification.showError(this.translate.instant('U_DEACTIVATE_OWN_ERROR'));
                      break;
                  case DeactivateUsersStatus.SECURITY_APPROVER_BLOCKED:
                      this.notification.showError(this.translate.instant('U_DEACTIVATE_SECURITY_APPROVER_ERROR'));
                      break;
              }
          }),
          catchError(() => {
              this.notification.showError(this.fDeactivateUnknownError);
              return EMPTY;
          })
        ).subscribe();
      }
    });
  }

  deleteSelectedUsers() {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      data: {
        title: this.translate.instant('U_DELETE_CONFIRM_TITLE'),
        content: this.translate.instant('U_DELETE_CONFIRM_CONTENT'),
        rejectBtnName: this.fNo,
        acceptBtnName: this.fYes,
        showRejectBtn: true,
      },
    });
    dialogRef.afterClosed().pipe(
      switchMap(result => {
        if (result) {
          return forkJoin(
            this.selection.selected.map(user => this.userService.deleteUser(user.id))
          );
        }
      })
    ).subscribe(() => {
      this.getUsers(true);
      this.notification.showSuccess(this.translate.instant('U_DELETED_SUCCESSFULLY'));
    })
  }

    expireUsersPasswords() {
        const dialogRef = this.dialog.open(AlertDialogComponent, {
            data: {
                title: this.translate.instant('manage-users.force-expire-dialog.title'),
                content: this.translate.instant('manage-users.force-expire-dialog.content'),
                rejectBtnName: this.fNo,
                acceptBtnName: this.fYes,
                showRejectBtn: true,
            },
        });

        dialogRef.afterClosed().pipe(
            switchMap(result => {
                if (result) {
                    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
                    const user_ids = this.selection.selected.map(user => user.id);
                    return this.userService.api_users_force_expire_passwords(user_ids, selectedOrg).pipe(
                        tap(() => {
                            this.getUsers(true);
                            this.selection.clear();
                            this.setExpirable();
                        }),
                        catchError(() => {
                            this.notification.showError(this.fUnknownError);
                            return EMPTY;
                        })
                    );
                }
                return EMPTY;
            })
        ).subscribe();
    }

    reactivateSelectedUsers() {
        const dialogRef = this.dialog.open(AlertDialogComponent, {
            data: {
                title: this.fReactivateTitle,
                content: this.fReactivateContent,
                rejectBtnName: this.fNo,
                acceptBtnName: this.fYes,
                showRejectBtn: true,
            },
        });

        dialogRef.afterClosed().pipe(
            filter(result => !!result),
            switchMap(() => {
                const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
                const user_ids = this.selection.selected.map(user => user.id);
                return this.userService.api_users_reactivate(user_ids, selectedOrg).pipe(
                    tap(() => {
                        this.getUsers(true);
                        this.selection.clear();
                    }),
                    catchError(() => {
                        this.notification.showError(this.fUnknownError);
                        return EMPTY;
                    })
                );
            })
        ).subscribe();
    }

  updateDisplayedColumns(columns: string[]) {
    this.displayedColumns = columns;
  }

  getApproverState(user) {
    let state: string;
    const securityApproverState = user?.security_approver ?? SecurityApproverState.NOT_APPROVER;
    switch (securityApproverState) {
      case SecurityApproverState.APPROVER:
        state = this.translate.instant('AIV_APPROVER');
        break;
      case SecurityApproverState.APPROVER_PENDING:
        state = this.translate.instant('security-approver.pending-removal');
        break;
      case SecurityApproverState.NOT_APPROVER_PENDING:
        state = this.translate.instant('security-approver.pending-approver');
        break;
      default:
        state = this.translate.instant('NONE');
    }

    return state;
  }

  isCigentUser(user) {
    return user.source === UserSource.Cigent;
  }

  getIdentitySource(user) {
    if (user.source in UserSource) {
      return UserSource[user.source];
    }
  }

  exportJSON() {
    const query = this.getQuery()
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
    query.pageSize = 0;
    query['selectedOrgId'] = selectedOrg;
    this.d3eService.get_threats_csv_token(query).subscribe({
      next: tokenResponse => {
        const url = this.userService.getExportUserJsonUrl(tokenResponse.token);
        multiDownload([url], {
          // tslint:disable-next-line:no-shadowed-variable
          rename: () => 'users.json',
        });
      }
    });
  }

  exportCSV() {
    const query = this.getQuery()
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
    query.pageSize = 0;
    query['columns'] = this.columns.filter(c => !!c.apiName);
    query['selectedOrgId'] = selectedOrg;
    this.d3eService.get_threats_csv_token(query).subscribe({
      next: tokenResponse => {
        const url = this.userService.getExportUserCsvUrl(tokenResponse.token);
        multiDownload([url], {
          // tslint:disable-next-line:no-shadowed-variable
          rename: () => 'users.csv',
        });
      }
    });
  }

  showSecurityApproval(user, event) {
    this.securityRequestService.getRequestsByTargetUser(user.id).pipe(
      switchMap(requests => {
        const approverRequests = requests.filter(
          request => request.type == SecurityRequestType.ADD_APPROVER || request.type == SecurityRequestType.REMOVE_APPROVER
        );
        return forkJoin([
          this.restConfigService.getRestConfig(),
          this.securityRequestService.getApproversStatus(approverRequests[0].id),
          this.securityRequestService.getRequestById(approverRequests[0].id),
          this.adminSettingsService.getAdminSettings(),
        ]);
      }),
      switchMap(([restConfig, approverStatus, srStatus, adminSettings]) => {
        if (!adminSettings) return;
        const dialogData = {
          results: approverStatus.results,
          minimumApprovalPercentRequired: restConfig.security_request_min_percent,
          securityApprovalMethod: adminSettings.security_approval_method,
          requestStatus: srStatus,
          dialogTitle: this.translate.instant('security-request-status.dialog-title'),
        };
        return this.dialog.open(SecurityRequestStatusDialogComponent, {width: '44em', data: dialogData}).afterClosed();
      })
    ).subscribe();
    event.stopPropagation();
  }

  protected readonly UserRole = UserRole;
  protected readonly SecurityApproverState = SecurityApproverState;
}
